import http from "./http";

/**
 * Bên ký (legal entity) · Uỷ quyền (mandate) · Chuỗi (chain) — Quân chốt 17/10/2026.
 * Backend: whalelo-backend/modules/legal-entity (README.md giải thích 3 khái niệm).
 *
 *   Legal entity = AI ký hợp đồng / nhận tiền / đóng thuế (công ty, hộ KD, cá nhân).
 *   Mandate      = QUAN HỆ: bên sở hữu điểm cho bên quản lý xem / ký thay (không tiền).
 *   Chain        = nhóm điểm hiển thị chung: tên chuỗi + bên quản lý.
 */

export type LegalEntityKind = "company" | "household" | "individual" | "unknown";
export type LegalEntityStatus = "draft" | "verified" | "archived";

export interface LegalEntityRow {
	id: number;
	kind: LegalEntityKind;
	legalName: string;
	registrationNo: string | null;
	taxId: string | null;
	registeredAddress: string | null;
	countryCode: string | null;
	representativeName: string | null;
	representativeEmail: string | null;
	status: LegalEntityStatus;
	mergedIntoId: number | null;
	source: string | null;
	note: string | null;
	soDiem?: number;
	dangBan?: number;
}

export interface LegalEntityPoint {
	id: number;
	name: string;
	lifecycleStatus: string;
	storeId: number | null;
	partnerRoleId: number | null;
	countryCode: string | null;
}

export interface MandateRow {
	id: number;
	owningEntityId: number;
	managingEntityId: number;
	storeId: number | null;
	scopeView: boolean;
	scopeSign: boolean;
	scopeMoney: boolean;
	authorityRef: string | null;
	validFrom: string;
	validTo: string | null;
}

export interface LegalEntityDetail {
	legalEntity: LegalEntityRow;
	points: LegalEntityPoint[];
	mandatesGiven: MandateRow[];
	mandatesReceived: MandateRow[];
}

export interface MergeSuggestion {
	soTaiKhoan: string;
	chuTaiKhoan: string | null;
	legalEntityIds: number[];
	partnerIds: number[];
}

export interface ChainRow {
	id: number;
	name: string | null;
	nameSuggestion: string | null;
	managingEntityId: number | null;
	managingEntityName: string | null;
	chainModeEnabled: boolean;
	storeOwnerPartnerId: number | null;
	/** 20/10/2026: tên + email trạm chủ chuỗi (máy chủ cũ chưa có ⇒ undefined, bảng chỉ hiện #id như trước). */
	ownerName?: string | null;
	ownerEmail?: string | null;
	/** 28/10/2026: chủ chuỗi vẫn là một TRẠM (toạ độ / published) ⇒ cần tách tài khoản chủ chuỗi. */
	ownerIsStation?: boolean;
	soDiem: number;
	dangBan: number;
}

export const listLegalEntities = async (params: { page?: number; perPage?: number; search?: string; status?: string }) => {
	const { data } = await http.get(`/admin/legal-entities`, { params });
	return data as { docs: LegalEntityRow[]; total: number; page: number; perPage: number };
};

export const getLegalEntity = async (id: number) => {
	const { data } = await http.get(`/admin/legal-entities/${id}`);
	return data as LegalEntityDetail;
};

export const updateLegalEntity = async (id: number, patch: Partial<LegalEntityRow>) => {
	const { data } = await http.patch(`/admin/legal-entities/${id}`, patch);
	return data as LegalEntityRow;
};

export const getMergeSuggestions = async () => {
	const { data } = await http.get(`/admin/legal-entities/merge-suggestions`);
	return (data?.groups || []) as MergeSuggestion[];
};

export const mergeLegalEntities = async (targetId: number, fromIds: number[]) => {
	const { data } = await http.post(`/admin/legal-entities/${targetId}/merge`, { fromIds });
	return data as { targetId: number; fromIds: number[]; diemChuyen: number; chuoiChuyen: number; uyQuyenBo: number };
};

/** Bên ký của một loạt trạm — cho cột Type ở Station Management (B2, 17/10/2026). */
export interface PartnerEntitySummary {
	partnerId: number;
	legalEntityId: number | null;
	legalName: string | null;
	status: LegalEntityStatus | null;
}
export const getEntitiesOfPartners = async (ids: number[]) => {
	if (!ids.length) return [] as PartnerEntitySummary[];
	const { data } = await http.get(`/admin/legal-entities/of-partners`, { params: { ids: ids.slice(0, 200).join(",") } });
	return (data?.docs || []) as PartnerEntitySummary[];
};

export const listChains = async () => {
	const { data } = await http.get(`/admin/chains`);
	return (data?.docs || []) as ChainRow[];
};

export const updateChainProfile = async (storeId: number, patch: { name?: string | null; managingEntityId?: number | null }) => {
	const { data } = await http.patch(`/admin/chains/${storeId}/profile`, patch);
	return data;
};

/** B4 (17/10/2026): gỡ một điểm khỏi chuỗi (về trạm lẻ). Không gỡ được trụ sở. */
export const removeChainLocation = async (storeId: number, partnerId: number) => {
	const { data } = await http.post(`/admin/chains/${storeId}/remove-location`, { partnerId });
	return data as { storeId: number; partnerId: number; donThoiGan: number; uyQuyenKetThuc: number };
};

/** 28/10/2026 (Quân chốt): tách tài khoản chủ chuỗi khỏi trạm — chủ chuỗi là tài khoản riêng, trạm cũ thành trạm thường. */
export const separateChainOwner = async (storeId: number, body: { newOwnerEmail: string; reason: string }) => {
	const { data } = await http.post(`/admin/chains/${storeId}/separate-owner`, body);
	return data?.data as {
		storeId: number;
		chainName: string;
		oldOwnerPartnerId: number;
		newOwnerPartnerId: number;
		toEmail: string;
		newOwnerCreated: boolean;
		emailSent: boolean;
	};
};

/** Lỗi API → câu tiếng Anh ngắn cho người vận hành. */
export const apiErrorText = (e: unknown): string => {
	const r = (e as { response?: { data?: { message?: string; code?: string } } })?.response?.data;
	return r?.message || r?.code || (e as Error)?.message || "Request failed";
};

/** 29/10/2026 (Quân chốt): khoá / mở rút tiền CẢ CHUỖI — chuỗi ký phụ lục ngoài hệ thống, ký xong thì mở. */
export interface ChainPayoutLock {
	storeId: number;
	chainName: string | null;
	stations: { partnerId: number; name: string | null; payoutLocked: boolean; chainLock: boolean; payoutLockReason: string | null }[];
	total: number;
	/** Trạm đang bị khoá bởi nút chuỗi (mở chuỗi sẽ gỡ). */
	chainLocked: number;
	/** Trạm bị khoá vì lý do khác (đổi ngân hàng…) — nút chuỗi không đụng. */
	otherLocked: number;
}

export const getChainPayoutLock = async (storeId: number) => {
	const { data } = await http.get(`/admin/chains/${storeId}/payout-lock`);
	return data?.data as ChainPayoutLock;
};

export const lockChainPayout = async (storeId: number, reason: string) => {
	const { data } = await http.post(`/admin/chains/${storeId}/payout-lock`, { reason });
	return data?.data as ChainPayoutLock & { lockedNow: number[] };
};

export const unlockChainPayout = async (storeId: number, note?: string) => {
	const { data } = await http.delete(`/admin/chains/${storeId}/payout-lock`, { data: { note } });
	return data?.data as ChainPayoutLock & { unlockedNow: number[] };
};

/* 03/11/2026 (Quân): Admin bật/tắt hộ 3 công tắc quyền tiền của chuỗi (cùng luật với chủ chuỗi). */
export interface ChainPermissions {
	locationsCanRequestPayout: boolean;
	locationsCanEditPayoutAccount: boolean;
	sharedPayoutAccount: boolean;
}

export const getChainPermissions = async (storeId: number) => {
	const { data } = await http.get(`/admin/chains/${storeId}/permissions`);
	return data?.data as ChainPermissions;
};

export const setChainPermissions = async (storeId: number, patch: Partial<ChainPermissions> & { reason?: string }) => {
	const { data } = await http.put(`/admin/chains/${storeId}/permissions`, patch);
	return data?.data as ChainPermissions;
};
